import groundcover from '@groundcover/browser';

/**
 * Fired when a logged-in user (session + RTK user loaded) should be identifiable in Heap
 * but the synchronous prerequisites failed (no `uid` / `id` / `window.heap` stub). We do **not**
 * emit this when `identify` was only deferred to `heap.onReady` — that case is still a "success" path.
 * In Groundcover RUM, look for custom event `heap_identify_skipped` and the `reason` attribute.
 *
 * This does **not** detect the case where `identify` ran (prerequisites met) but Heap still
 * shows an anonymous / unidentified user (e.g. blocked beacon, product-side merge, or ITP):
 * for that, use the browser network tab on `heap-api.com` or Heap’s delivery tools.
 *
 * PII: only booleans and reason codes—no sub, email, or uid in attributes.
 *
 * Gated on `groundcoverApiKey` from public config: these events are sent via `groundcover.sendCustomEvent`
 * (RUM), not Heap’s `track` API, so we only attempt them when Groundcover is configured in this environment.
 */
export type HeapIdentifySkipReason =
    | 'no_public_heap_id'
    | 'missing_id'
    | 'missing_uid'
    | 'heap_global_missing';

export function reportHeapIdentifySkipped(
    groundcoverApiKey: string | undefined,
    params: {
        hasHeapId: boolean;
        hasUserId: boolean;
        hasUid: boolean;
        hasSub: boolean;
        hasWindowHeap: boolean;
    },
): void {
    if (!groundcoverApiKey) {
        return;
    }
    const { hasHeapId, hasUserId, hasUid, hasSub, hasWindowHeap } = params;

    let reason: HeapIdentifySkipReason;
    if (!hasHeapId) {
        reason = 'no_public_heap_id';
    }
    else if (!hasUserId) {
        reason = 'missing_id';
    }
    else if (!hasUid) {
        reason = 'missing_uid';
    }
    else if (!hasWindowHeap) {
        reason = 'heap_global_missing';
    }
    else {
        // Defensive: caller should not invoke when all prerequisites for identify exist.
        return;
    }

    const attributes: Record<string, string | boolean> = {
        reason,
        has_heap_id: hasHeapId,
        has_user_id: hasUserId,
        has_uid: hasUid,
        has_sub: hasSub,
        has_window_heap: hasWindowHeap,
    };
    try {
        groundcover.sendCustomEvent({
            event: 'heap_identify_skipped',
            attributes,
        });
    }
    catch (e) {
        if (import.meta.env.DEV) {
            console.warn('[heap telemetry] sendCustomEvent failed', e);
        }
    }
}

/** `heap.onReady` never invoked the callback within `stallAfterMs` (e.g. blocked `heap_config.js`, network). */
export function reportHeapIdentifyDeferredStall(
    groundcoverApiKey: string | undefined,
    stallAfterMs: number,
): void {
    if (!groundcoverApiKey) {
        return;
    }
    try {
        groundcover.sendCustomEvent({
            event: 'heap_identify_deferred_stall',
            attributes: { stall_after_ms: stallAfterMs, used_on_ready: true } satisfies Record<string, string | number | boolean>,
        });
    }
    catch (e) {
        if (import.meta.env.DEV) {
            console.warn('[heap telemetry] sendCustomEvent failed', e);
        }
    }
}

/** The deferred `identify` / `addUserProperties` callback threw. */
export function reportHeapIdentifyDeferredError(
    groundcoverApiKey: string | undefined,
    message: string,
): void {
    if (!groundcoverApiKey) {
        return;
    }
    try {
        groundcover.sendCustomEvent({
            event: 'heap_identify_deferred_error',
            attributes: { message: message.slice(0, 500) },
        });
    }
    catch (e) {
        if (import.meta.env.DEV) {
            console.warn('[heap telemetry] sendCustomEvent failed', e);
        }
    }
}
